import { ApiClient } from '../apiClient';
import { Card, PaginatedCards, CardFilters, BrandWithMetadata, CategoryWithMetadata } from '../../types/card';
import categoriesData from '../../utils/categories.json';
import { getPosthogDistinctId } from '../../lib/posthog-utils';
import {
  ProductUpdateChangeInput,
  ProductUpdateSubscribeInput,
  ProductUpdateSubscriptionView,
} from '../../types/productUpdates';

/* eslint-disable */
export class CardService {
  constructor(private apiClient: ApiClient) {}

  // A grid needs little of a card; the rest (description, terms, the
  // 12-locale i18n block) would ride every listing payload.
  private mapCardDTO(cards: PaginatedCards) {
    return {
      ...cards,
      items: cards.items.map((card: Card) => ({
        _id: card._id,
        slug: card.slug,
        name: card.name,
        images: card.images,
        countries: card.countries,
        category: card.category,
        availability: card.availability,
        discountPct: card.discountPct,
        rating: card.rating,
        createdAt: card.createdAt,
        updatedAt: card.updatedAt,
      })) as Card[]
    };
  }
  /**
   * Get all cards with optional filtering
   */
  async getCards(options: CardFilters = {}): Promise<PaginatedCards> {
    // console.log(' CardService.getCards called with options:', options);
    try {
      const result = await this.apiClient.get<PaginatedCards>('/gift/cards', options);

      return this.mapCardDTO(result);
    } catch (error) {

      return {
        items: [],
        total: 0,
        totalPages: 0,
        page: 0,
        limit: 20
      };
    }
  }

  /**
   * Get cards with discounts
   */
  async getDiscountCards(options: { 
    limit?: number; 
    offset?: number 
  } = {}): Promise<PaginatedCards> {
    return this.apiClient.get<PaginatedCards>('/gift/card-discounts', options);
  }

  /**
   * Get a card by its slug (a former slug answers too)
   */
  async getCardByBrand(slug: string): Promise<Card> {
    return this.apiClient.get<Card>(`/gift/card/byBrand/${encodeURIComponent(slug)}`);
  }

  /**
   * Register a product view (beacon). Decoupled from the cached PDP read.
   */
  async registerView(slug: string): Promise<void> {
    return this.apiClient.post<void>(`/gift/card/byBrand/${encodeURIComponent(slug)}/view`);
  }

  async getCardsByIds(ids: string[]): Promise<Card[]> {
    if (!ids || ids.length === 0) {
      throw new Error('Card IDs are required');
    }
    return this.apiClient.post<Card[]>('/gift/cards', { ids });
  }

  /**
   * Search for cards, optionally narrowed by the listing filters
   */
  async searchCards(
    query: string,
    filters: Pick<CardFilters, 'country' | 'category' | 'discount' | 'instock'> = {}
  ): Promise<Card[]> {
    const response = await this.apiClient.get<Card[]>(`/gift/card/search/${query}`, filters);
    return response || [];
  }

  /**
   * Get card categories with metadata
   */
  async getCategories(): Promise<CategoryWithMetadata[]> {
    return this.apiClient.get<CategoryWithMetadata[]>('/gift/card/categories');
  }

  /**
   * Get card categories as string array (from local JSON - no API call)
   */
  async getCategoriesAsStrings(): Promise<string[]> {
    return categoriesData.map(category => category.Slug);
  }

  /**
   * Get cached countries from cookie
   */
  private getCachedCountriesFromCookie(): string[] | null {
    if (typeof window === 'undefined') return null; // Server-side
    
    const cached = document.cookie
      .split('; ')
      .find(row => row.startsWith('countries_cache='))
      ?.split('=')[1];

    if (!cached) return null;

    try {
      const parsed = JSON.parse(decodeURIComponent(cached));
      const now = Date.now();
      
      // Check if cache is still valid (5 minutes)
      if (now - parsed.timestamp < 5 * 60 * 1000) {
        // console.log('🍪 Using cached countries from cookie');
        return parsed.data;
      } else {
        // Clear expired cache
        document.cookie = 'countries_cache=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
        return null;
      }
    } catch {
      return null;
    }
  }

  /**
   * Cache countries in cookie
   */
  private setCachedCountriesToCookie(countries: string[]): void {
    if (typeof window === 'undefined') return; // Server-side
    
    const cacheData = {
      data: countries,
      timestamp: Date.now()
    };
    
    const expires = new Date(Date.now() + 5 * 60 * 1000).toUTCString(); // 5 minutes
    document.cookie = `countries_cache=${encodeURIComponent(JSON.stringify(cacheData))}; expires=${expires}; path=/`;
    // console.log('🍪 Countries cached in cookie for 5 minutes');
  }

  /**
   * Get card countries (with client-side cookie caching)
   */
  async getCountries(): Promise<string[]> {
    // First check client-side cookie cache
    const cachedCountries = this.getCachedCountriesFromCookie();
    if (cachedCountries) {
      return cachedCountries;
    }

    // If no cache, fetch directly from backend API
    // console.log('🌐 Fetching countries from backend API (cache miss)');
    const countries = await this.apiClient.get<string[]>('/gift/card/countries');
    
    // Cache the result in cookie for client-side
    this.setCachedCountriesToCookie(countries);
    
    return countries;
  }

  /**
   * Get all product brands with metadata
   */
  async getBrands(): Promise<BrandWithMetadata[]> {
    return this.apiClient.get<BrandWithMetadata[]>('/gift/brands');
  }

  /**
   * Get all product brands as string array (legacy compatibility)
   */
  async getBrandsAsStrings(): Promise<string[]> {
    const brands = await this.getBrands();
    return brands.map(brand => brand.name);
  }

  /**
   * Get cards with same brand
   */
  async getCardsWithSameBrand(id: string): Promise<Card[]> {
    return this.apiClient.get<Card[]>(`/gift/cards-similar-brand/${id}`);
  }

  /**
   * Subscribe to a card's updates (back in stock, discount, new amounts),
   * for one amount (sku) or the whole card. A second press replaces the
   * events and the mode; empty events stop. The device's PostHog id rides
   * along, as on the cart calls, so the browsing actor merges with the
   * address.
   */
  async subscribeProductUpdates(cardId: string, input: ProductUpdateSubscribeInput): Promise<unknown> {
    const posthogDistinctId = getPosthogDistinctId();
    return this.apiClient.post<unknown>(`/gift/card/${cardId}/updates`, {
      ...(input.email && { email: input.email }),
      ...(input.sku && { sku: input.sku }),
      events: input.events,
      ...(input.mode && { mode: input.mode }),
      ...(posthogDistinctId && { posthogDistinctId }),
    });
  }

  /**
   * Manage-link endpoints: the signed token from an update email's footer
   * proves control of the address, no session needed.
   */
  async manageUpdates(email: string, token: string): Promise<ProductUpdateSubscriptionView[]> {
    return this.apiClient.post<ProductUpdateSubscriptionView[]>('/gift/updates/manage', { email, token });
  }

  async changeUpdate(
    email: string,
    token: string,
    change: Omit<ProductUpdateChangeInput, 'mode'>,
  ): Promise<ProductUpdateSubscriptionView[]> {
    return this.apiClient.post<ProductUpdateSubscriptionView[]>('/gift/updates/change', {
      email,
      token,
      productId: change.productId,
      ...(change.sku && { sku: change.sku }),
      events: change.events,
    });
  }

  async stopAllUpdates(email: string, token: string): Promise<{ stopped: number }> {
    return this.apiClient.post<{ stopped: number }>('/gift/updates/stop-all', { email, token });
  }

  /**
   * Account endpoints: the same three for the signed-in customer (cookies).
   */
  async getAccountUpdates(): Promise<ProductUpdateSubscriptionView[]> {
    return this.apiClient.get<ProductUpdateSubscriptionView[]>('/gift/updates/account');
  }

  async changeAccountUpdate(change: ProductUpdateChangeInput): Promise<ProductUpdateSubscriptionView[]> {
    return this.apiClient.put<ProductUpdateSubscriptionView[]>('/gift/updates/account', {
      productId: change.productId,
      ...(change.sku && { sku: change.sku }),
      events: change.events,
      ...(change.mode && { mode: change.mode }),
    });
  }

  async stopAccountUpdate(productId: string, sku?: string): Promise<ProductUpdateSubscriptionView[]> {
    const query = sku ? `?sku=${encodeURIComponent(sku)}` : '';
    return this.apiClient.delete<ProductUpdateSubscriptionView[]>(
      `/gift/updates/account/${encodeURIComponent(productId)}${query}`,
    );
  }
}